import { addSheetBackLink, PARENT_SHEETS } from "../sheetNavigation.js";
import {
  BACK_ROW,
  BODY_FONT,
  HEADER_FONT_12,
  HEADER_ROW,
  NA_FILL,
  SPACER_ROW,
  TEMPLATE_ROW,
  TITLE_ROW,
  VALUE_FILL,
  applyColumnLayout,
  addTitleRow,
  hideColumnsAfter,
  hideRowsThrough,
  setCell,
  setupSheetView,
  THIN,
} from "./detailSheetCommon.js";

export const COL_D = 4;
export const COL_E = 5;
export const COL_F = 6;
export const COL_G = 7;
export const COL_H = 8;
export const COL_I = 9;
export const COL_J = 10;
export const COL_K = 11;
export const COL_L = 12;
export const COL_M = 13;
export const COL_N = 14;

const MEDIUM = { style: "medium", color: { argb: "FF000000" } };

function parseBorderSpec(spec) {
  const map = { t: THIN, m: MEDIUM };
  return {
    top: spec[0] !== "-" ? map[spec[0]] : undefined,
    left: spec[1] !== "-" ? map[spec[1]] : undefined,
    bottom: spec[2] !== "-" ? map[spec[2]] : undefined,
    right: spec[3] !== "-" ? map[spec[3]] : undefined,
  };
}

export function applyBorderPattern(ws, row, colStart, patterns) {
  patterns.forEach((spec, index) => {
    ws.getCell(row, colStart + index).border = parseBorderSpec(spec);
  });
}

export function outerRowPatterns(colStart, colEnd, { first = "ttt-", middle = "t-t-", last = "t-tt" } = {}) {
  const patterns = [];
  for (let col = colStart; col <= colEnd; col += 1) {
    if (col === colStart) patterns.push(first);
    else if (col === colEnd) patterns.push(last);
    else patterns.push(middle);
  }
  return patterns;
}

export function setupPrinciple2DetailSheet(
  ws,
  {
    widths,
    colEnd,
    ySplit = 11,
    topLeftCell = "D12",
    hiddenCols,
    lastVisibleCol,
    xSplit = 3,
  }
) {
  applyColumnLayout(ws, widths, hiddenCols ?? ["A", "B"]);
  if (lastVisibleCol != null) hideColumnsAfter(ws, lastVisibleCol);
  setupSheetView(ws, { ySplit, topLeftCell, zoomScale: 80, xSplit });
  hideRowsThrough(ws, 5);
}

export { hideRowsThrough };

export function addPrinciple2BackLink(ws, colEnd) {
  addSheetBackLink(ws, PARENT_SHEETS.PRINCIPLE_2, {
    row: BACK_ROW,
    col: colEnd,
    mergeEnd: colEnd,
    align: "right",
    parentCell: "D8",
  });
}

export function applyMergedRowBorder(ws, row, colStart, colEnd, patterns) {
  if (!patterns?.length) return;
  if (colStart >= colEnd) {
    ws.getCell(row, colStart).border = parseBorderSpec(patterns[0]);
    return;
  }
  const left = parseBorderSpec(patterns[0]);
  const right = parseBorderSpec(patterns[patterns.length - 1]);
  ws.getCell(row, colStart).border = {
    top: left.top ?? right.top,
    left: left.left,
    bottom: left.bottom ?? right.bottom,
    right: right.right,
  };
}

export function addSheetTitle(ws, colEnd, title, { height = 30, borderPatterns } = {}) {
  addTitleRow(ws, COL_D, colEnd, title, { height });
  const patterns = borderPatterns ?? outerRowPatterns(COL_D, colEnd);
  applyMergedRowBorder(ws, TITLE_ROW, COL_D, colEnd, patterns);
}

export function addSimpleTableHeader(ws, colStart, colEnd, headers, { height = 15.5, patterns } = {}) {
  applyBorderPattern(
    ws,
    HEADER_ROW,
    colStart,
    patterns ?? Array(colEnd - colStart + 1).fill("tttt")
  );
  ws.getRow(HEADER_ROW).height = height;
  headers.forEach(({ col, label, font = HEADER_FONT_12, bold = false, align = "center" }) => {
    setCell(ws, HEADER_ROW, col, label, { font, bold, bordered: false, align });
  });
}

export function addHiddenTemplateRow(
  ws,
  colStart,
  colEnd,
  { row = TEMPLATE_ROW, height = 15.5, naCols = [], value = ".", fillCols = [], patterns } = {}
) {
  applyBorderPattern(
    ws,
    row,
    colStart,
    patterns ?? Array(colEnd - colStart + 1).fill("tttt")
  );
  for (let col = colStart; col <= colEnd; col += 1) {
    let fill;
    if (naCols.includes(col)) fill = NA_FILL;
    else if (fillCols.includes(col)) fill = VALUE_FILL;
    setCell(ws, row, col, value, { bordered: false, fill, font: BODY_FONT });
  }
  ws.getRow(row).height = height;
  ws.getRow(row).hidden = true;
}

export function addTableSpacer(ws, colStart, colEnd, { row = SPACER_ROW, height = 30, patterns } = {}) {
  ws.mergeCells(row, colStart, row, colEnd);
  applyMergedRowBorder(ws, row, colStart, colEnd, patterns ?? outerRowPatterns(colStart, colEnd));
  ws.getRow(row).height = height;
}

export function displayText(value) {
  if (value === null || value === undefined || value === "") return ".";
  return String(value);
}

export function toYesNo(value) {
  if (value === true || value === "true" || value === "yes" || value === "Yes") return "Yes";
  if (value === false || value === "false" || value === "no" || value === "No") return "No";
  return ".";
}

export function formatPercent(value) {
  if (value === null || value === undefined || value === "") return ".";
  const str = String(value);
  return str.includes("%") ? str : `${str}%`;
}

export function formatNumber(value) {
  if (value === null || value === undefined || value === "") return ".";
  const num = Number(value);
  if (!Number.isNaN(num)) return num.toFixed(2);
  return String(value);
}

export function writeDataRow(ws, row, colStart, colEnd, columns, { patterns } = {}) {
  applyBorderPattern(
    ws,
    row,
    colStart,
    patterns ?? Array(colEnd - colStart + 1).fill("tttt")
  );
  columns.forEach(
    ({ col, value, align = "left", fill = VALUE_FILL, font = BODY_FONT, vertical = "middle" }) => {
      setCell(ws, row, col, value, {
        align,
        vertical,
        fill: fill === undefined ? undefined : fill,
        font,
        bordered: false,
        wrapText: true,
      });
    }
  );
}
